import type { AssetManifest } from '@kenocyber/shared';
import { mapCatalogEntry } from '@kenocyber/shared';
import Phaser from 'phaser';
import { assetUrl, findAnimatedAsset } from './assets';

/**
 * **A arte de um alienígena e de um boss sai do `mapIndex` dele, e de mais nada.**
 *
 * ## O defeito que este módulo fecha (gate V28I.BI)
 *
 * O mundo aberto carregava UMA folha de alienígena e UMA de boss, as duas
 * resolvidas por `mapCatalogEntry(1)`, no `preload` da cena. Todos os vinte
 * alienígenas eram desenhados com `alien1` e todos os vinte bosses com `boss1`,
 * apesar de os quarenta mestres existirem e de o pipeline já os publicar.
 *
 * A razão do erro é a mesma que já valia para os cascos e está escrita no
 * `preload`: **o `preload` corre antes de o servidor dizer o que há no
 * quadrante**. Quando ele corre, nenhum `mapIndex` é conhecido — e o índice 1
 * foi escrito como o único que se podia saber de antemão. Isso deixou de ser
 * verdade no instante em que a arte dos vinte mapas passou a existir.
 *
 * ## A relação é total e é a canónica
 *
 * `mapCatalogEntry(N).mobAssetId` e `.bossAssetId` — a MESMA fonte que decide o
 * `mapIndex` mecânico, a vida, a escala e a espécie. Não há aqui segunda tabela
 * de identidade visual, e não pode haver: um mapa que mudasse de arte mudaria
 * num sítio só.
 *
 * ## Nenhum recuo silencioso para o índice 1
 *
 * Um índice fora de 1–50 é recusado por `mapCatalogEntry`, que lança. Um índice
 * de 21 a 50 tem `assetId` nulo — arte que ainda não existe — e é recusado aqui,
 * com o índice no texto. Quem chama apanha a recusa e reporta-a por
 * `open-world:scene-error`, **uma vez por índice**. Desenhar `alien1` no lugar
 * de `alien7` seria mentir sobre que criatura está no ecrã, e é exatamente o que
 * este gate veio corrigir.
 *
 * ## O pedido é sob procura, como o dos cascos e o do drone
 *
 * A mesma disciplina de `requestDroneTexture`: pede-se a folha uma vez, a chave
 * fica no conjunto de pendentes até o ficheiro chegar, a animação é criada no
 * instante em que a folha existe, e uma falha de rede larga a chave em vez de a
 * deixar presa para sempre. O corpo desenha-se com um marcador de lugar até lá.
 *
 * Uma expedição carrega o que a banda de perigo daquele quadrante puser à vista
 * — quatro mapas, quando muito — e não os vinte.
 */

/** As duas famílias que um mapa tem. A chave e o `assetId` saem do mesmo sítio. */
export type OpenWorldMapBodyKind = 'mob' | 'boss';

/** A chave da textura desta família neste mapa. Derivada, nunca escrita à mão. */
export function openWorldMapBodyTextureKey(kind: OpenWorldMapBodyKind, mapIndex: number): string {
  return `open-world-${kind}-${mapIndex}`;
}

/** A chave da animação. A mesma derivação, com um sufixo. */
export function openWorldMapBodyAnimKey(kind: OpenWorldMapBodyKind, mapIndex: number): string {
  return `${openWorldMapBodyTextureKey(kind, mapIndex)}-idle`;
}

/** O `assetId` do manifesto para esta família neste mapa, ou uma recusa. */
function assetIdFor(kind: OpenWorldMapBodyKind, mapIndex: number): string {
  /* Lança para um índice fora de [1, 50]. A recusa é do catálogo, e é uma só. */
  const entry = mapCatalogEntry(mapIndex);
  const assetId = kind === 'mob' ? entry.mobAssetId : entry.bossAssetId;
  if (assetId === null) {
    throw new RangeError(
      `open world map ${mapIndex} has no ${kind} art; it is reserved for after the closed alpha`,
    );
  }
  return assetId;
}

/**
 * **Pede a folha 3x3 desta família neste mapa, uma vez.**
 *
 * Idempotente: uma textura que já existe ou já foi pedida devolve sem fazer
 * nada, portanto chamar isto uma vez por criatura por quadro custa duas buscas
 * num mapa.
 *
 * Lança quando o índice não tem arte. Quem chama decide o que fazer com a
 * recusa — e este módulo não escolhe outra imagem por ele.
 */
export function requestOpenWorldMapBody(input: {
  readonly scene: Phaser.Scene;
  readonly manifest: AssetManifest;
  readonly kind: OpenWorldMapBodyKind;
  readonly mapIndex: number;
  /** O conjunto de chaves em voo da cena. O mesmo que os cascos usam. */
  readonly pending: Set<string>;
}): void {
  const key = openWorldMapBodyTextureKey(input.kind, input.mapIndex);
  if (input.scene.textures.exists(key) || input.pending.has(key)) {
    return;
  }
  const asset = findAnimatedAsset(input.manifest, assetIdFor(input.kind, input.mapIndex));
  const animKey = openWorldMapBodyAnimKey(input.kind, input.mapIndex);
  input.pending.add(key);
  input.scene.load.spritesheet(key, assetUrl(input.manifest, asset), {
    frameWidth: asset.layout.frameWidth,
    frameHeight: asset.layout.frameHeight,
  });
  input.scene.load.once(`filecomplete-spritesheet-${key}`, () => {
    input.pending.delete(key);
    /*
     * **A animação nasce com a folha**, e não no `create` da cena: antes de o
     * ficheiro chegar não há quadros para numerar. A cadência vem dos metadados
     * do recurso, como manda a disciplina de recursos — nunca de um número
     * escrito aqui.
     */
    if (input.scene.anims.exists(animKey)) {
      return;
    }
    input.scene.anims.create({
      key: animKey,
      frames: input.scene.anims.generateFrameNumbers(key, {
        start: 0,
        end: asset.layout.frameCount - 1,
      }),
      frameRate: asset.animations[0]?.frameRate ?? 8,
      repeat: -1,
    });
  });
  /*
   * A mesma rede que os cascos e o drone têm: se o ficheiro falhar, o evento
   * acima nunca chega e a chave ficaria pendente para sempre — e o corpo nunca
   * mais seria pedido nem substituído.
   */
  input.scene.load.once(Phaser.Loader.Events.COMPLETE, () => input.pending.delete(key));
  input.scene.load.start();
}
